/**
 * Drag-and-drop wiring for the Product Map grouped sortable list.
 *
 * One `DndContext` (owned by `ProductMapPage`) handles two interactions,
 * disambiguated by `active.data.current.type`:
 *  - `capability` — cross-group sort. Ported from the tasks/suite `Board`
 *    (`apps/web/src/ui/components/board/Board.tsx`): a three-state
 *    `dragSnapshot` (idle `null` / mid-drag spliced / post-drop settling)
 *    plus `deriveDropPosition`/`sameIdOrder` from `board/dropPosition` — see
 *    the state-by-state comments below for why each piece exists. `Board.tsx`
 *    itself is NOT reused directly (its `PointerSensor` can't be driven by
 *    synthetic events in tests, so it isn't touched here) — this hook
 *    reimplements just its splice/settle mechanics against the map's own
 *    shape: "columns" keyed by area id, plus one sentinel column for the
 *    Unassigned bucket.
 *  - `area` — reorder by the area's own handle, a separate, non-nested
 *    `SortableContext` of area ids. This is a same-column reorder in a single
 *    flat list, so — exactly as `Board.tsx`'s `handleDragOver` comment notes
 *    for same-column moves — dnd-kit's own `verticalListSortingStrategy`
 *    already renders a correct live preview with no splicing needed. It DOES
 *    still need the post-drop half of the three-state treatment though:
 *    `areaOrderSnapshot` is the area-order analog of `dragSnapshot`'s
 *    "settling" state (no mid-drag "dragging" state, since there's nothing to
 *    splice). Without it, the render right after `dragEnd` — before
 *    `useReorderAreas`'s optimistic write lands a microtask later — resets
 *    dnd-kit's sortable transforms against the still-pre-drag `map.areas`
 *    order, snapping the dragged area back before it jumps to its real new
 *    slot: the flicker this state exists to prevent.
 *
 * `deriveDropPosition` special-cases an `overId` that starts with `column-`
 * (dropped on a container's empty space, not on a specific card) — but this
 * feature's container ids are `area-container:…` / the Unassigned sentinel
 * (see `areaContainerId` in `productMapLogic.ts`, also used as `AreaRow`'s
 * `useDroppable` id). Rather than reformat `areaContainerId`'s id space to
 * match `column-` — which would ripple into `resolveCapabilityDrop` and its
 * tests — this hook translates at the one call site: a raw `over.id` that
 * `parseContainerAreaId` recognizes as a container is rewritten to
 * `column-${...}` only for the `deriveDropPosition` call.
 *
 * @module features/product-map/hooks/useProductMapDragDrop
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragCancelEvent,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { deriveDropPosition, sameIdOrder } from '../../../ui/components/board/dropPosition';
import { parseContainerAreaId } from '../productMapLogic';
import { useMoveCapability, useReorderAreas } from '../useProductMap';
import type { ProductAreaWithCapabilities, ProductCapability, ProductMapResponse } from '../types';

export type ProductMapDragType = 'capability' | 'area';

export interface ProductMapDragData {
  type: ProductMapDragType;
  /** Display label, used by the drag overlay. */
  label?: string;
}

export interface ActiveProductMapDrag {
  type: ProductMapDragType;
  id: string;
  label?: string;
}

function dragData(event: { data: { current?: unknown } }): ProductMapDragData | undefined {
  return event.data.current as ProductMapDragData | undefined;
}

/** Sentinel column id for the Unassigned bucket — never collides with a real (prefixed) area id. */
const UNASSIGNED_COLUMN_ID = '__unassigned__';

/**
 * How long the post-drop snapshot may shadow the live map while waiting for
 * `useMoveCapability`'s optimistic cache write to land. Mirrors
 * `Board.tsx`'s `SETTLE_TIMEOUT_MS`: comfortably past the drop animation,
 * short enough that an unpersisted/rolled-back drop still visibly reverts
 * quickly.
 */
const SETTLE_TIMEOUT_MS = 500;

type CapabilitiesByColumn = Record<string, ProductCapability[]>;

interface DragSnapshot {
  capabilitiesByColumn: CapabilitiesByColumn;
}

function columnIdFor(areaId: string | null): string {
  return areaId ?? UNASSIGNED_COLUMN_ID;
}

function buildColumns(map: ProductMapResponse | undefined): CapabilitiesByColumn {
  const byColumn: CapabilitiesByColumn = {};
  if (!map) return byColumn;
  for (const area of map.areas) byColumn[area.id] = area.capabilities;
  byColumn[UNASSIGNED_COLUMN_ID] = map.unassignedCapabilities;
  return byColumn;
}

function findColumnOfCapability(columns: CapabilitiesByColumn, capabilityId: string): string | null {
  for (const [columnId, items] of Object.entries(columns)) {
    if (items.some((c) => c.id === capabilityId)) return columnId;
  }
  return null;
}

/** Resolves a dnd-kit `over.id` (container id or capability id) to a column id. */
function resolveColumnId(columns: CapabilitiesByColumn, overId: string): string | null {
  const containerAreaId = parseContainerAreaId(overId);
  if (containerAreaId !== undefined) return columnIdFor(containerAreaId);
  return findColumnOfCapability(columns, overId);
}

/**
 * The single source of truth for "what order are areas in right now" — fed to
 * BOTH `ProductMapPage`'s `<SortableContext items={…}>` and the `areasForRender`
 * array built below. A `SortableContext` whose `items` disagree with the
 * actually-rendered order is its own source of jumpiness, so there must be
 * exactly one derivation, used in both places.
 *
 * `snapshot` (see `areaOrderSnapshot`) wins whenever set — that's the eagerly
 * -published post-drop order, held until `liveAreaIds` converges with it.
 * Reconciled defensively against `liveAreaIds` (mirrors
 * `applyOptimisticAreaReorder`'s reconciliation in `productMapLogic.ts`): an
 * id the snapshot has that's no longer live (an area archived mid-settle) is
 * dropped, and any live id the snapshot doesn't yet know about (a new area
 * created mid-settle) is appended — so a stale-but-not-yet-cleared snapshot
 * can never hide or duplicate a real area.
 */
export function resolveRenderedAreaIds(
  liveAreaIds: readonly string[],
  snapshot: readonly string[] | null
): string[] {
  if (!snapshot) return [...liveAreaIds];
  const liveSet = new Set(liveAreaIds);
  const reconciled = snapshot.filter((id) => liveSet.has(id));
  for (const id of liveAreaIds) {
    if (!reconciled.includes(id)) reconciled.push(id);
  }
  return reconciled;
}

export function useProductMapDragDrop(propertyId: string, map: ProductMapResponse | undefined) {
  const moveCapability = useMoveCapability(propertyId);
  const reorderAreas = useReorderAreas(propertyId);

  const [activeDrag, setActiveDrag] = useState<ActiveProductMapDrag | null>(null);
  // Transient snapshot of column->capabilities, three states — see module
  // docblock: idle `null` (render straight from `map`), dragging (live
  // mid-drag splice from `onDragOver`), settling (post-drop snapshot held
  // until `map` converges or the timeout fires).
  const [dragSnapshot, setDragSnapshot] = useState<DragSnapshot | null>(null);
  // The touched columns from the last accepted drop — settle compares ONLY
  // these, mirroring `Board.tsx`'s `lastDropRef` so an unrelated realtime
  // update elsewhere on the map can't block convergence.
  const lastDropRef = useRef<{ from: string; to: string } | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearSettleTimer, [clearSettleTimer]);

  // Area-order analog of `dragSnapshot`'s settling state — see module
  // docblock. Non-null means "there was a drop; hold this order until
  // `liveAreaIds` converges with it or the timeout fires." Unlike the
  // capability path there's no separate "touched columns" bookkeeping: area
  // reorder is one flat list, so the snapshot itself IS the thing compared
  // for convergence (no `lastDropRef` needed).
  const [areaOrderSnapshot, setAreaOrderSnapshot] = useState<string[] | null>(null);
  const areaSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAreaSettleTimer = useCallback(() => {
    if (areaSettleTimerRef.current !== null) {
      clearTimeout(areaSettleTimerRef.current);
      areaSettleTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearAreaSettleTimer, [clearAreaSettleTimer]);

  const liveAreaIds = useMemo(() => map?.areas.map((a) => a.id) ?? [], [map]);
  // Single source of truth for area order — feeds BOTH `ProductMapPage`'s
  // `<SortableContext items={areaIds}>` and `areasForRender` below. See
  // `resolveRenderedAreaIds`'s docblock for why there must be exactly one.
  const areaIds = useMemo(
    () => resolveRenderedAreaIds(liveAreaIds, areaOrderSnapshot),
    [liveAreaIds, areaOrderSnapshot]
  );
  const areasById = useMemo(() => new Map((map?.areas ?? []).map((a) => [a.id, a])), [map]);
  const liveColumns = useMemo(() => buildColumns(map), [map]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = dragData(event.active);
      if (!data) return;
      setActiveDrag({ type: data.type, id: String(event.active.id), label: data.label });
      if (data.type === 'capability') {
        clearSettleTimer();
        lastDropRef.current = null;
        setDragSnapshot({ capabilitiesByColumn: liveColumns });
      }
    },
    [liveColumns, clearSettleTimer]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over) return;
      const data = dragData(active);
      // Area reorder is same-column-only (one flat list) — dnd-kit's own
      // sortable strategy already previews it live; splicing here would only
      // fight that (the exact feedback loop `Board.tsx` calls out for
      // same-column moves).
      if (!data || data.type !== 'capability') return;

      const activeId = String(active.id);
      const overId = String(over.id);

      setDragSnapshot((current) => {
        const base = current ?? { capabilitiesByColumn: liveColumns };
        const to = resolveColumnId(base.capabilitiesByColumn, overId);
        if (!to) return base;
        const from = findColumnOfCapability(base.capabilitiesByColumn, activeId);
        if (!from || from === to) return base;

        const movingItem = base.capabilitiesByColumn[from]?.find((c) => c.id === activeId);
        if (!movingItem) return base;

        const isContainerDrop = parseContainerAreaId(overId) !== undefined;
        const targetWithoutActive = (base.capabilitiesByColumn[to] ?? []).filter(
          (c) => c.id !== activeId
        );
        let insertAt: number;
        if (isContainerDrop) {
          insertAt = targetWithoutActive.length;
        } else {
          const idx = targetWithoutActive.findIndex((c) => c.id === overId);
          insertAt = idx < 0 ? targetWithoutActive.length : idx;
        }

        // Re-parent the moving item's `areaId` too, so a render mid-drag
        // (before drop) shows it belonging to the hovered area — matters for
        // anything keyed off `capability.areaId` rather than which column
        // array it's currently sitting in.
        const reparented: ProductCapability = {
          ...movingItem,
          areaId: to === UNASSIGNED_COLUMN_ID ? null : to,
        };

        return {
          capabilitiesByColumn: {
            ...base.capabilitiesByColumn,
            [from]: base.capabilitiesByColumn[from]!.filter((c) => c.id !== activeId),
            [to]: [
              ...targetWithoutActive.slice(0, insertAt),
              reparented,
              ...targetWithoutActive.slice(insertAt),
            ],
          },
        };
      });
    },
    [liveColumns]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const data = dragData(active);
      setActiveDrag(null);

      if (data?.type === 'area') {
        // Areas never used a mid-drag SPLICE snapshot (see module docblock),
        // so clear a STALE one left behind by a capability drag that never
        // got a drop (dragStart always sets one; if this area drag started
        // afterward without a capability handleDragEnd running, it's stale).
        // Guarded on `lastDropRef` so this can't cut off a capability drop
        // that's still settling (waiting for its optimistic write to land) —
        // clearing that early would reintroduce the identical flicker this
        // whole area-order fix exists to prevent, just for the other type.
        if (lastDropRef.current === null) {
          setDragSnapshot(null);
        }

        // A NO-OP path here (no `over`, unresolvable ids, or dropped back on
        // its own slot) means THIS drag never produced a new order — so it
        // never owns anything in `areaOrderSnapshot`. If a live
        // `areaSettleTimerRef` says a PREVIOUS drop is still waiting on its
        // optimistic write, that snapshot must be left alone: clearing it
        // here would render the not-yet-converged `liveAreaIds` and
        // reintroduce the exact snap-back flicker this whole state exists to
        // prevent — e.g. picking an area back up and releasing it in the
        // same slot within the 500ms settle window of the PRIOR drop.
        const clearAreaSnapshotIfIdle = () => {
          if (areaSettleTimerRef.current === null) setAreaOrderSnapshot(null);
        };

        if (!over) {
          clearAreaSnapshotIfIdle();
          return;
        }
        const activeId = String(active.id);
        const overId = String(over.id);
        // Indices are resolved against the CURRENTLY RENDERED order
        // (`areaIds`, which is what `<SortableContext>` was given) — the
        // same list dnd-kit computed `active`/`over` against — not a fresh
        // re-derivation from `map`, which could already differ if a prior
        // drop is still settling.
        const oldIndex = areaIds.indexOf(activeId);
        const newIndex = areaIds.indexOf(overId);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
          clearAreaSnapshotIfIdle();
          return;
        }

        const nextOrder = arrayMove(areaIds, oldIndex, newIndex);

        // Eagerly publish the dropped order so the very next render — before
        // `useReorderAreas`'s optimistic write lands (its `onMutate` awaits
        // `cancelQueries`, a microtask later) — already shows areas in their
        // dropped order. Without this, `verticalListSortingStrategy` resets
        // sortable transforms against the still-pre-drag `map.areas` order,
        // producing the flicker: snap back, then jump to the real slot once
        // the optimistic write lands. The settle effect below clears the
        // snapshot once `liveAreaIds` actually matches it.
        setAreaOrderSnapshot(nextOrder);
        reorderAreas.mutate(nextOrder);
        clearAreaSettleTimer();
        areaSettleTimerRef.current = setTimeout(() => {
          areaSettleTimerRef.current = null;
          setAreaOrderSnapshot(null);
        }, SETTLE_TIMEOUT_MS);
        return;
      }

      if (!over) {
        setDragSnapshot(null);
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      const snapshot = dragSnapshot ?? { capabilitiesByColumn: liveColumns };

      // `from` is resolved against the PRE-drag live map (not the snapshot)
      // — it's used as `deriveDropPosition`'s no-op guard baseline and as the
      // move mutation's origin column, both of which care about where the
      // item started, not where it's been spliced to mid-drag.
      const from = findColumnOfCapability(liveColumns, activeId);
      const to = resolveColumnId(snapshot.capabilitiesByColumn, overId);
      if (!from || !to) {
        setDragSnapshot(null);
        return;
      }

      const isContainerDrop = parseContainerAreaId(overId) !== undefined;
      // Translate to `deriveDropPosition`'s `column-` convention — see module
      // docblock for why this is done here rather than reshaping
      // `areaContainerId` itself.
      const dropOverId = isContainerDrop ? `column-${overId}` : overId;

      const dropPosition = deriveDropPosition({
        fromColumnId: from,
        toColumnId: to,
        overId: dropOverId,
        activeId,
        originalItems: liveColumns[to] ?? [],
        targetSnapshotItems: snapshot.capabilitiesByColumn[to] ?? liveColumns[to] ?? [],
        getItemId: (c) => c.id,
        overSortableIndex: (over.data.current?.sortable as { index?: unknown } | undefined)?.index,
      });

      if (!dropPosition) {
        setDragSnapshot(null);
        return;
      }

      const { finalItems, beforeItemId, afterItemId } = dropPosition;

      // Eagerly publish the final order into dragSnapshot so the very next
      // render — which happens BEFORE `useMoveCapability`'s `onMutate` finishes
      // its optimistic cache write (it awaits `cancelQueries`, so the write
      // lands a microtask later) — already shows the row in its dropped slot.
      // Without this, `verticalListSortingStrategy` resets neighbor transforms
      // with the pre-drop order still current, producing a visible hop before
      // the cache write lands. The settle effect below clears the snapshot
      // once the live map actually matches it.
      const finalColumns: CapabilitiesByColumn = { ...snapshot.capabilitiesByColumn, [to]: finalItems };
      if (from !== to) {
        finalColumns[from] = (snapshot.capabilitiesByColumn[from] ?? liveColumns[from] ?? []).filter(
          (c) => c.id !== activeId
        );
      }

      moveCapability.mutate({
        id: activeId,
        areaId: to === UNASSIGNED_COLUMN_ID ? null : to,
        beforeCapabilityId: beforeItemId,
        afterCapabilityId: afterItemId,
      });

      lastDropRef.current = { from, to };
      setDragSnapshot({ capabilitiesByColumn: finalColumns });
      clearSettleTimer();
      settleTimerRef.current = setTimeout(() => {
        settleTimerRef.current = null;
        lastDropRef.current = null;
        setDragSnapshot(null);
      }, SETTLE_TIMEOUT_MS);
    },
    [areaIds, liveColumns, dragSnapshot, moveCapability, reorderAreas, clearSettleTimer, clearAreaSettleTimer]
  );

  const handleDragCancel = useCallback(
    (event: DragCancelEvent) => {
      setActiveDrag(null);
      const data = dragData(event.active);

      if (data?.type === 'area') {
        // A cancelled area drag never reached a successful drop, so — same
        // as handleDragEnd's area branch — it never wrote to ANY of this
        // state itself: not dragSnapshot/lastDropRef/settleTimer (areas only
        // touch those defensively, guarded by `lastDropRef`, to mop up a
        // truly stale leftover), and not areaOrderSnapshot/areaSettleTimerRef
        // (those are only ever written by a successful drop). A live settle
        // from a PREVIOUS drop — of either kind — must be left to finish on
        // its own timer; cancelling this drag can't be allowed to wipe it
        // and render the not-yet-converged live order.
        if (lastDropRef.current === null) setDragSnapshot(null);
        if (areaSettleTimerRef.current === null) setAreaOrderSnapshot(null);
        return;
      }

      // Capability drags claim dragSnapshot/lastDropRef/settleTimer for
      // themselves at dragStart (see handleDragStart), so by the time a
      // cancel fires this drag unambiguously owns them — clearing here is
      // correct. Capability drags never touch area-order state, so a
      // pending area settle (if any) is left untouched.
      clearSettleTimer();
      lastDropRef.current = null;
      setDragSnapshot(null);
    },
    [clearSettleTimer]
  );

  // Settle: once the live map's touched columns actually match the dropped
  // order in id-sequence — NOT "a render happened" — drop the snapshot so
  // future updates (external edits, the eventual invalidate-driven refetch)
  // flow through unmediated. An id-order comparison is required because the
  // optimistic write lands a flush or two after dragEnd; clearing on the
  // first render would snap the list back to the pre-drag order mid drop
  // animation. If the map never converges (rolled-back mutation), the
  // SETTLE_TIMEOUT_MS timer started at dragEnd clears the snapshot instead.
  useEffect(() => {
    if (activeDrag !== null) return;
    if (dragSnapshot === null) return;
    const drop = lastDropRef.current;
    if (!drop) return;
    const idOf = (c: ProductCapability) => c.id;
    const converged =
      sameIdOrder(liveColumns[drop.to] ?? [], dragSnapshot.capabilitiesByColumn[drop.to] ?? [], idOf) &&
      (drop.from === drop.to ||
        sameIdOrder(liveColumns[drop.from] ?? [], dragSnapshot.capabilitiesByColumn[drop.from] ?? [], idOf));
    if (!converged) return;
    clearSettleTimer();
    lastDropRef.current = null;
    setDragSnapshot(null);
  }, [liveColumns, activeDrag, dragSnapshot, clearSettleTimer]);

  // Settle for area order: same reasoning as the capability settle above,
  // but simpler — area reorder touches the WHOLE flat list (not per-touched
  // columns), so `areaOrderSnapshot` itself IS the thing compared for
  // convergence; no separate "what did the last drop touch" ref is needed.
  useEffect(() => {
    if (activeDrag !== null) return;
    if (areaOrderSnapshot === null) return;
    if (!sameIdOrder(liveAreaIds, areaOrderSnapshot, (id) => id)) return;
    clearAreaSettleTimer();
    setAreaOrderSnapshot(null);
  }, [liveAreaIds, activeDrag, areaOrderSnapshot, clearAreaSettleTimer]);

  const renderColumns = dragSnapshot?.capabilitiesByColumn ?? liveColumns;

  const areasForRender: ProductAreaWithCapabilities[] = areaIds.map((id) => {
    const area = areasById.get(id)!;
    return { ...area, capabilities: renderColumns[id] ?? area.capabilities };
  });
  const unassignedForRender = renderColumns[UNASSIGNED_COLUMN_ID] ?? map?.unassignedCapabilities ?? [];

  return {
    sensors,
    areaIds,
    activeDrag,
    areasForRender,
    unassignedForRender,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
}
